import styles from './numInput.module.scss';
import formStyles from '../formStyles.module.scss';

/**
 * Component for rendering a numeric input field.
 *
 * @param props - The component props.
 * @param props.id - String for input id.
 * @param props.name - String for input name.
 * @param props.title - String for title label text.
 * @param props.min - String of a number for input min value.
 * @param props.max - String of a number for input max value.
 * @param props.required - Boolean to set if it is required to submit.
 * @param props.onChange - onChange function passed to component for form control.
 * @param props.autoComplete - autoComplete attribute for the input element.
 * @param props.placeholder - Placeholder text for the input element.
 * @returns - The rendered NumInput component.
 *
 * @example
 * ```tsx
 * <NumInput
 *    id="numInput"
 *    name="numInput"
 *    title="Number Input"
 *    min="1"
 *    max="10"
 *    onChange={handleChange}
 * />
 * ```
 */
const NumInput = ({
    id,
    name,
    title,
    required = false,
    min = '0',
    max = '10',
    onChange,
    autoComplete = 'off',
    placeholder = '',
    value,
}: {
    id: string;
    name: string;
    title: string;
    min?: string;
    max?: string;
    required?: boolean;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    autoComplete?: string;
    placeholder?: string;
    value?: string | number;
}) => {
    return (
        <div data-testid="numinput" className={formStyles.inputcontainer}>
            <label
                data-testid="numinputlabel"
                htmlFor={id}
                className={formStyles.label}
            >
                {title}
            </label>
            <input
                onChange={onChange && onChange}
                data-testid="numinputinput"
                type="number"
                min={min}
                max={max}
                id={id}
                name={name}
                required={required}
                className={`${formStyles.input} ${styles.noSpinner}`}
                autoComplete={autoComplete}
                placeholder={placeholder}
                value={value}
                onKeyDown={(e) => {
                    // Prevent 'e', 'E', '+', '.' and '-' unless at the start for negative numbers
                    if (['e', 'E', '+', '.', '-'].includes(e.key)) {
                        e.preventDefault();
                    }
                }}
            />
        </div>
    );
};

export default NumInput;
